import { Controller } from "@hotwired/stimulus"

export default class extends Controller {
  connect() {
    this.draggedElement = null;
    this.draggedClone = null; // Used to show a "shadow copy" of the original as you drag it, only for <select> elements
    this.startingClientX = 0;
    this.startingClientY = 0;
    this.endingClientX = 0;
    this.endingClientY = 0;

    this.form = this.element;

    this.inputs = [];
    this.activateElements();

    // Listen for new elements being added to the page.
    const observer = new MutationObserver(this.activateElements.bind(this));
    observer.observe(this.element, { childList: true, subtree: true });
  }

  activateElements() {
    // Find all the input elements and make them draggable.
    const foundInputs = this.element.querySelectorAll("input, select, .signature-placeholder");
    foundInputs.forEach(input => {
      // Skip inputs we've already processed.
      if (this.inputs.includes(input)) {
        return;
      }
      // Skip hidden inputs
      if (input.type === "hidden") {
        return;
      }

      input.classList.add("cursor-grab");

      input.draggable = true;

      // Most inputs support drag events.  But select elements require a different approach.
      if (input.tagName === "SELECT") {
        input.addEventListener("mousedown", this.onMouseDown.bind(this));
      } else {
        input.addEventListener("dragstart", this.onDragStart.bind(this));
        input.addEventListener("dragover", this.onDragOver.bind(this));
        input.addEventListener("dragend", this.onDragEnd.bind(this));
      }

      this.inputs.push(input);
    });
  }

  onMouseDown(e) {
    e.preventDefault();

    this.draggedElement = e.target;
    this.startingClientX = e.clientX;
    this.startingClientY = e.clientY;

    // Create a clone of the dragged element to show as a "shadow" while dragging
    let original = this.draggedElement.closest(".input-container");
    let originalRect = original.getBoundingClientRect();

    let page = this.draggedElement.closest(".charity-care-application-form-page");
    let pageRect = page.getBoundingClientRect();

    let absoluteX = originalRect.left - pageRect.left;
    let absoluteY = originalRect.top - pageRect.top;

    this.draggedClone = original.cloneNode(true);
    this.draggedClone.classList.add("opacity-50");
    this.draggedClone.style.position = "absolute";
    this.draggedClone.style.zIndex = 1000;
    this.draggedClone.style.bottom = null; // Normally we style using bottom, but this interferes with dragging, which uses top and left
    this.draggedClone.style.left = `${absoluteX}px`;
    this.draggedClone.style.top = `${absoluteY}px`;
    // Save the original position as a data attribute
    this.draggedClone.setAttribute("data-original-left", absoluteX);
    this.draggedClone.setAttribute("data-original-top", absoluteY);

    // Add the clone to the body
    this.draggedElement.parentNode.parentNode.appendChild(this.draggedClone);

    document.addEventListener('mousemove', this.onMouseMove.bind(this));

    document.addEventListener('mouseup', this.onMouseUp.bind(this), { once: true });
  }

  onMouseMove(e) {
    // Reposition the clone to follow the mouse
    if (this.draggedClone) {
      let deltaX = e.clientX - this.startingClientX;
      let deltaY = e.clientY - this.startingClientY;

      let newX = parseInt(this.draggedClone.getAttribute("data-original-left")) + deltaX;
      let newY = parseInt(this.draggedClone.getAttribute("data-original-top")) + deltaY;

      this.draggedClone.style.left = `${newX}px`;
      this.draggedClone.style.top = `${newY}px`;
    }
  }

  onMouseUp(e) {
    document.removeEventListener('mousemove', this.onMouseMove.bind(this));

    let positionChanged = this.repositionInputContainer(this.draggedElement, e.clientX, e.clientY);
    if (positionChanged) {
      this.form.requestSubmit();
    }
    if (this.draggedClone) {
      this.draggedClone.remove();
      this.draggedClone = null;
    }
  }

  onDragStart(e) {
    e.dataTransfer.setData("text/plain", e.target.id); // Needed for Firefox
    this.draggedElement = e.target;
    this.startingClientX = e.clientX;
    this.startingClientY = e.clientY;
  }

  onDragOver(e) {
    e.preventDefault();
  }

  // This method does the following:
  // 1. Repositions the input element to the new dragged position
  // 2. Updates the hidden inputs with the new position
  // It returns true if the position changed, false otherwise.
  repositionInputContainer(element, clientX, clientY) {
    if (element == this.draggedElement && clientX == this.endingClientX && clientY == this.endingClientY) {
      return false;
    }

    this.endingClientX = clientX;
    this.endingClientY = clientY;

    // On the server we store following for each coordinate:
    // - bottom_left_x_pt
    // - bottom_left_y_pt
    // These represent the (X, Y) coordinates of the input as measured from the bottm-left corner
    // of the page, and defined as "points".  On the client the input is positioned using CSS
    // styles as a percentage of the page width/height.
    // 
    // When we display the input container, we use CSS styles to position it absolutely on the page,
    // but in this case we use the bottom-left corner of the page as the anchor point.  This requires
    // doing some (1-Y) math to convert the Y coordinate.

    let inputContainer = element.closest(".input-container");
    let coordinateId = inputContainer.getAttribute("data-coordinate-id");
    let xHiddenInput = document.querySelector(`[name="coordinates[${coordinateId}][bottom_left_x_pt]"`);
    let yHiddenInput = document.querySelector(`[name="coordinates[${coordinateId}][bottom_left_y_pt]"`);
    let originalXPoints = parseFloat(xHiddenInput.value);
    let originalYPoints = parseFloat(yHiddenInput.value);

    // Find the containing page
    let page = element.closest(".charity-care-application-form-page");
    let pageRect = page.getBoundingClientRect();

    let xConversionFactor, yConversionFactor;
    if (pageRect.height > pageRect.width) {
      xConversionFactor = 612;
      yConversionFactor = 792;
    } else {
      xConversionFactor = 792;
      yConversionFactor = 612;
    }

    let originalXPercent = originalXPoints / xConversionFactor;
    let originalYPercent = originalYPoints / yConversionFactor;

    // Compute the delta between the starting position and the current position
    let deltaX = this.endingClientX - this.startingClientX;
    let deltaY = this.endingClientY - this.startingClientY;
    // Convert this to a percentage of the page width/height
    // Find the client X/Y bounding box of the page

    // Compute the percentage of the page width/height
    let deltaXPercent = deltaX / pageRect.width;
    let deltaYPercent = deltaY / pageRect.height;
    // Add this to the original position of the input container
    let xPercent = originalXPercent + deltaXPercent;
    let yPercent = originalYPercent - deltaYPercent; // We subtract because we're using the bottom-left corner as the anchor point

    inputContainer.style.left = `${xPercent * 100}%`;
    inputContainer.style.bottom = `${yPercent * 100}%`;

    // Store the new values in the hidden inputs.
    xHiddenInput.value = parseInt(xPercent * xConversionFactor);
    yHiddenInput.value = parseInt(yPercent * yConversionFactor);

    return true;
  }

  onDragEnd(e) {
    // Note that dragend seems to be called twice, so we rely on positionChanged to decide whether to submit the form.
    let positionChanged = this.repositionInputContainer(e.target, e.clientX, e.clientY);
    if (positionChanged) {
      this.form.requestSubmit();
    }
  }
}